import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Title,
  Table,
  TextInput,
  Button,
  Paper,
  Group,
  Text,
  Badge,
  Loader,
  Stack,
  Divider,
  Pagination,
  ActionIcon,
  Tooltip,
  Tabs,
  Select,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useDisclosure } from "@mantine/hooks";
import {
  IconBarcode,
  IconRefresh,
  IconLock,
  IconFilter,
  IconAlertTriangle,
  IconFlask,
  IconDownload,
} from "@tabler/icons-react";
import {
  useCuttingQueuePage,
  useOpenCaseByBarcode,
} from "../../hooks/useWorkflows";
import {
  CaseStatusBadge,
  FilterModal,
  ActiveFilters,
} from "../../components/cases";
import { useAuthStore } from "../../store/auth.store";
import { type CaseFilters } from "../../services";
import {
  isCompleteCassetteBarcode,
  useBarcodeScanner,
} from "../../hooks/useBarcodeScanner";
import { formatDateOnlyForDisplay, parseDateOnly } from "../../utils/dateOnly";
import { formatLabDateTime } from "../../utils/labTime";
import {
  cuttingService,
  type WorkflowQueueCase,
} from "../../services/workflows.service";
import {
  clearCuttingQueueView,
  DEFAULT_CUTTING_VIEW,
  readCuttingQueueView,
  saveCuttingQueueView,
  type CuttingQueueView,
  type CuttingSort,
  type CuttingTab,
} from "./cuttingQueueView";

const ITEMS_PER_PAGE = 20;
type AdditionalStainTarget = NonNullable<
  WorkflowQueueCase["additionalStainTargets"]
>[number];

function formatQueueDate(value: unknown): string {
  const dateOnly = parseDateOnly(value);
  return dateOnly ? formatDateOnlyForDisplay(dateOnly) : "—";
}

export default function CuttingQueuePage() {
  const navigate = useNavigate();
  const currentUserId = useAuthStore((state) => state.profile?.id);
  const [barcodeInput, setBarcodeInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [savedViews, setSavedViews] = useState(() => ({
    userId: currentUserId,
    active: readCuttingQueueView(currentUserId, "active"),
    additional: readCuttingQueueView(currentUserId, "additional"),
  }));
  const [page, setPage] = useState(1);
  const [blockedPage, setBlockedPage] = useState(1);
  const [activeTab, setActiveTab] = useState<CuttingTab>("active");
  const [isExporting, setIsExporting] = useState(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const views = useMemo(
    () =>
      savedViews.userId === currentUserId
        ? savedViews
        : {
            userId: currentUserId,
            active: readCuttingQueueView(currentUserId, "active"),
            additional: readCuttingQueueView(currentUserId, "additional"),
          },
    [currentUserId, savedViews]
  );
  const { filters, sort } = views[activeTab];

  const updateView = useCallback(
    (next: CuttingQueueView) => {
      setSavedViews({ ...views, [activeTab]: next });
      saveCuttingQueueView(currentUserId, activeTab, next);
    },
    [activeTab, currentUserId, views]
  );

  const queueRequest = useMemo(
    () => ({
      activePage: page,
      blockedPage,
      limit: ITEMS_PER_PAGE,
      sort,
      filters:
        activeTab === "additional"
          ? { ...filters, isAdditionalStain: true }
          : filters,
    }),
    [activeTab, blockedPage, filters, page, sort]
  );
  const { data, isLoading, isFetching, refetch } =
    useCuttingQueuePage(queueRequest);
  const openCaseMutation = useOpenCaseByBarcode();

  const activeCases = data?.active.items ?? [];
  const blockedCases = data?.blocked.items ?? [];
  const activeTotal = data?.active.total ?? 0;
  const blockedTotal = data?.blocked.total ?? 0;
  const totalActivePages = data?.active.totalPages ?? 0;
  const totalBlockedPages = data?.blocked.totalPages ?? 0;
  const isAdditionalStainsTab = activeTab === "additional";

  // Check if any filters are active (excluding search)
  const hasActiveFilters = Object.values(filters).some((value) => {
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.length > 0;
    if (typeof value === "boolean") return value;
    return !!value;
  });

  const handleFiltersApply = useCallback(
    (newFilters: CaseFilters) => {
      if (Object.keys(newFilters).length === 0) {
        updateView(DEFAULT_CUTTING_VIEW);
        clearCuttingQueueView(currentUserId, activeTab);
      } else {
        updateView({ filters: newFilters, sort });
      }
      setPage(1);
      setBlockedPage(1);
    },
    [activeTab, currentUserId, sort, updateView]
  );

  const handleActiveFiltersChange = useCallback(
    (newFilters: CaseFilters) => {
      updateView({ filters: newFilters, sort });
      setPage(1);
      setBlockedPage(1);
    },
    [sort, updateView]
  );

  const handleClearAllFilters = useCallback(() => {
    updateView(DEFAULT_CUTTING_VIEW);
    clearCuttingQueueView(currentUserId, activeTab);
    setPage(1);
    setBlockedPage(1);
  }, [activeTab, currentUserId, updateView]);

  const handleSortChange = useCallback(
    (value: string | null) => {
      const nextSort = value as CuttingSort | null;
      if (!nextSort) return;
      updateView({ filters, sort: nextSort });
      setPage(1);
      setBlockedPage(1);
    },
    [filters, updateView]
  );

  const handleTabChange = useCallback((value: string | null) => {
    setActiveTab(value === "additional" ? "additional" : "active");
    setPage(1);
    setBlockedPage(1);
  }, []);

  const handleExportAdditionalStains = useCallback(async () => {
    setIsExporting(true);
    try {
      await cuttingService.exportAdditionalStains(filters);
      notifications.show({
        title: "Additional stains exported",
        message:
          "The complete block pull list was downloaded as an Excel file.",
        color: "green",
      });
    } catch {
      notifications.show({
        title: "Export failed",
        message: "The additional stains list could not be exported. Try again.",
        color: "red",
      });
    } finally {
      setIsExporting(false);
    }
  }, [filters]);

  const handleBarcodeSubmit = async () => {
    if (!barcodeInput.trim()) {
      return;
    }

    setError(null);

    try {
      const parts = barcodeInput.trim().split(";");
      if (parts.length !== 3) {
        throw new Error(
          "Invalid barcode format. Expected: {caseNumber};{specimenLetter};{blockNumber}"
        );
      }

      const [, scannedSpec, scannedBlock] = parts;
      const caseData = await openCaseMutation.mutateAsync({
        barcode: barcodeInput.trim(),
        intent: "cutting",
      });
      navigate(`/cutting/${caseData.id}?block=${scannedSpec}-${scannedBlock}`);
    } catch (err: any) {
      setError(err.message || "Failed to open case");
      setBarcodeInput("");
    }
  };

  const handleBarcodeKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleBarcodeSubmit();
    }
  };

  useBarcodeScanner(
    useCallback(
      async (barcode: string) => {
        setBarcodeInput(barcode);
        setError(null);
        try {
          const parts = barcode.split(";");
          if (parts.length !== 3) {
            throw new Error(
              "Invalid barcode format. Expected: {caseNumber};{specimenLetter};{blockNumber}"
            );
          }
          const [, scannedSpec, scannedBlock] = parts;
          const caseData = await openCaseMutation.mutateAsync({
            barcode,
            intent: "cutting",
          });
          navigate(
            `/cutting/${caseData.id}?block=${scannedSpec}-${scannedBlock}`
          );
        } catch (err: any) {
          setError(err.message || "Failed to open case");
          setBarcodeInput("");
        }
      },
      [openCaseMutation, navigate]
    ),
    {
      enabled: !openCaseMutation.isPending,
      ownerKey: "cutting-queue",
      isCompleteBarcode: isCompleteCassetteBarcode,
    }
  );

  const handleRowClick = (caseId: string, lockedByOther: boolean) => {
    if (lockedByOther) {
      navigate(`/cases/${caseId}`);
    } else {
      navigate(`/cutting/${caseId}`);
    }
  };

  const renderTableHeader = () => (
    <Table.Thead>
      <Table.Tr>
        <Table.Th>Case</Table.Th>
        <Table.Th>Patient</Table.Th>
        <Table.Th>Procedure</Table.Th>
        <Table.Th style={{ whiteSpace: "nowrap" }}>Status</Table.Th>
        <Table.Th>Priority</Table.Th>
        <Table.Th>Pathologist</Table.Th>
        {!isAdditionalStainsTab && <Table.Th>Facility</Table.Th>}
        <Table.Th>
          {isAdditionalStainsTab ? "Required specimens & blocks" : "Specimens"}
        </Table.Th>
        {isAdditionalStainsTab && <Table.Th>Requested</Table.Th>}
        <Table.Th>Action</Table.Th>
      </Table.Tr>
    </Table.Thead>
  );

  const renderCaseRow = (caseItem: any, isBlockedTable = false) => {
    const isLockedByOther =
      caseItem.lockedBy && caseItem.lockedBy !== currentUserId;
    const isCutting = caseItem.currentStatus?.name === "Cutting";
    const isAdditionalStain = caseItem.isAdditionalStain === true;
    // Derive mixed-slide status from slides array returned by the API
    const slides: any[] = caseItem.slides || [];
    const hasMixedSlides =
      slides.some((s: any) => s.isCompleted) &&
      slides.some((s: any) => !s.isCompleted);

    return (
      <Table.Tr
        key={caseItem.id}
        style={{
          cursor: isBlockedTable ? "not-allowed" : "pointer",
          opacity: isLockedByOther ? 0.6 : 1,
        }}
        onClick={() =>
          !isBlockedTable && handleRowClick(caseItem.id, isLockedByOther)
        }
      >
        {/* Case: Case # + External # + Date */}
        <Table.Td>
          <Stack gap={2}>
            <Group gap={6} wrap="wrap">
              <Text size="sm" fw={500}>
                {isAdditionalStain
                  ? caseItem.parentCaseNumber || caseItem.caseNumber
                  : caseItem.caseNumber}
              </Text>
              {isAdditionalStain && (
                <Badge
                  size="sm"
                  color="grape"
                  variant="light"
                  leftSection={<IconFlask size={11} />}
                >
                  Additional stain
                </Badge>
              )}
            </Group>
            {caseItem.externalCaseNumber && (
              <Badge size="xs" variant="outline" color="gray">
                Ext: {caseItem.externalCaseNumber}
              </Badge>
            )}
            <Text size="xs" c="dimmed">
              {formatQueueDate(caseItem.dateReceived)}
            </Text>
          </Stack>
        </Table.Td>
        {/* Patient: Name + DOB + MRN */}
        <Table.Td>
          {caseItem.patient ? (
            <Stack gap={0}>
              <Text size="sm">
                {caseItem.patient.lastName}, {caseItem.patient.firstName}
              </Text>
              {caseItem.patient.dateOfBirth && (
                <Text size="xs" c="dimmed">
                  DOB: {formatQueueDate(caseItem.patient.dateOfBirth)}
                </Text>
              )}
              {caseItem.patient.medicalRecordNumber && (
                <Text size="xs" c="dimmed">
                  MRN: {caseItem.patient.medicalRecordNumber}
                </Text>
              )}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed">
              —
            </Text>
          )}
        </Table.Td>
        {/* Procedure */}
        <Table.Td style={{ wordBreak: "break-word" }}>
          <Text size="sm">{caseItem.procedure?.name || "—"}</Text>
        </Table.Td>
        {/* Status: Lock + Status badge */}
        <Table.Td style={{ whiteSpace: "nowrap" }}>
          <Group gap={4} wrap="nowrap">
            {caseItem.lockedBy && (
              <IconLock
                size={14}
                color={isLockedByOther ? "orange" : "green"}
              />
            )}
            <CaseStatusBadge
              statusName={caseItem.currentStatus?.name || "Unknown"}
              isBlocked={caseItem.isBlocked}
            />
          </Group>
        </Table.Td>
        {/* Priority: Rush/High Priority/Normal */}
        <Table.Td>
          {caseItem.isRush || caseItem.isHighPriority ? (
            <Group gap={4} wrap="nowrap">
              {caseItem.isRush && (
                <Tooltip
                  label={
                    caseItem.rushReason || "Rush case — expedited processing"
                  }
                  multiline
                  maw={300}
                >
                  <Badge color="red" variant="filled" size="xs">
                    RUSH
                  </Badge>
                </Tooltip>
              )}
              {caseItem.isHighPriority && (
                <Tooltip
                  label={
                    caseItem.highPriorityReason ||
                    "High priority — flagged for attention"
                  }
                  multiline
                  maw={300}
                >
                  <Badge color="orange" variant="filled" size="xs">
                    HIGH
                  </Badge>
                </Tooltip>
              )}
            </Group>
          ) : (
            <Text size="sm" c="dimmed">
              Normal
            </Text>
          )}
        </Table.Td>
        {/* Pathologist */}
        <Table.Td>
          <Text size="sm">
            {caseItem.pathologist?.fullName || "Unassigned"}
          </Text>
        </Table.Td>
        {!isAdditionalStainsTab && (
          <Table.Td>
            <Text size="sm">{caseItem.facility?.name || "—"}</Text>
          </Table.Td>
        )}
        {/* Specimens & Blocks */}
        <Table.Td>
          <Stack gap={0}>
            {isAdditionalStain ? (
              (caseItem.additionalStainTargets || []).map(
                (target: AdditionalStainTarget) => (
                  <Text
                    key={`${target.specimenNumber}-${target.blockNumber}`}
                    size="sm"
                  >
                    {target.specimenNumber}-{target.blockNumber}
                    {target.stainCodes?.length
                      ? ` · ${target.stainCodes.join(", ")}`
                      : ""}
                  </Text>
                )
              )
            ) : (
              <>
                <Text size="sm">{caseItem.specimenCount || 0} specimens</Text>
                <Text size="sm" c="dimmed">
                  {caseItem.blockCount || 0} blocks
                </Text>
              </>
            )}
            {hasMixedSlides && (
              <Badge size="xs" color="violet" variant="light" mt={2}>
                Mixed
              </Badge>
            )}
          </Stack>
        </Table.Td>
        {isAdditionalStainsTab && (
          <Table.Td>
            <Stack gap={0}>
              {(caseItem.additionalStainTargets || []).map(
                (target: AdditionalStainTarget) => (
                  <Text
                    key={`${target.specimenNumber}-${target.blockNumber}`}
                    size="sm"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {target.requestedAt
                      ? formatLabDateTime(target.requestedAt)
                      : "—"}
                  </Text>
                )
              )}
            </Stack>
          </Table.Td>
        )}
        <Table.Td>
          <Button
            size="xs"
            variant={isBlockedTable || isLockedByOther ? "subtle" : "light"}
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                isBlockedTable || isLockedByOther
                  ? `/cases/${caseItem.id}${
                      isBlockedTable ? "?tab=errors" : ""
                    }`
                  : `/cutting/${caseItem.id}`
              );
            }}
          >
            {isBlockedTable || isLockedByOther
              ? "View"
              : isCutting
              ? "Continue"
              : "Start"}
          </Button>
        </Table.Td>
      </Table.Tr>
    );
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          <Group justify="space-between">
            <Title order={2}>Cutting Queue</Title>
          </Group>

          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tabs.List>
              <Tabs.Tab value="active">Active Queue</Tabs.Tab>
              <Tabs.Tab value="additional">Additional Stains</Tabs.Tab>
            </Tabs.List>
          </Tabs>

          {/* Barcode Scanner and Filters Row */}
          <Group align="stretch" grow>
            {/* Barcode Scanner Input */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconBarcode size={24} />
                  <Text fw={500}>Scan Cassette Barcode</Text>
                </Group>
                <Group>
                  <TextInput
                    placeholder="Scan or enter barcode (e.g., G25-00001;A;1)"
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    onKeyPress={handleBarcodeKeyPress}
                    style={{ flex: 1 }}
                    autoFocus
                    error={error}
                  />
                  <Button
                    onClick={handleBarcodeSubmit}
                    loading={openCaseMutation.isPending}
                    disabled={!barcodeInput.trim()}
                  >
                    Open Case
                  </Button>
                </Group>
                <Text size="sm" c="dimmed">
                  Scan a cassette barcode to open the case and auto-print slide
                  labels
                </Text>
              </Stack>
            </Paper>

            {/* Queue Filters */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={24} />
                    <Text fw={500}>Queue Filters</Text>
                  </Group>
                  <ActionIcon
                    variant="subtle"
                    size="lg"
                    onClick={() => refetch()}
                    loading={isFetching}
                  >
                    <IconRefresh size={18} />
                  </ActionIcon>
                </Group>
                <Button variant="light" onClick={openFilterModal} fullWidth>
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                <Select
                  label="Sort cases"
                  data={[
                    { value: "received-asc", label: "Received (oldest first)" },
                    { value: "case-asc", label: "Case number (A–Z)" },
                    { value: "case-desc", label: "Case number (Z–A)" },
                  ]}
                  value={sort}
                  onChange={handleSortChange}
                  allowDeselect={false}
                  comboboxProps={{
                    position: "bottom-end",
                    offset: 8,
                    withinPortal: true,
                  }}
                />
                {hasActiveFilters && (
                  <ActiveFilters
                    filters={filters}
                    onFiltersChange={handleActiveFiltersChange}
                    onClearAll={handleClearAllFilters}
                  />
                )}
                {!hasActiveFilters && sort !== DEFAULT_CUTTING_VIEW.sort && (
                  <Button variant="subtle" onClick={handleClearAllFilters}>
                    Clear view
                  </Button>
                )}
                <Text size="sm" c="dimmed">
                  Showing {activeTotal + blockedTotal} cases
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Active Cases Table */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Group gap="sm">
                  <Text fw={500} size="lg">
                    {isAdditionalStainsTab
                      ? "Additional Stains"
                      : "Active Queue"}{" "}
                    ({activeTotal})
                  </Text>
                  {isAdditionalStainsTab && (
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconDownload size={14} />}
                      onClick={handleExportAdditionalStains}
                      loading={isExporting}
                    >
                      Export Excel
                    </Button>
                  )}
                </Group>
                {totalActivePages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(page * ITEMS_PER_PAGE, activeTotal)} of{" "}
                      {activeTotal} cases
                    </Text>
                    <Pagination
                      total={totalActivePages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {isLoading ? (
                <Group justify="center" p="xl">
                  <Loader />
                </Group>
              ) : activeCases.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters
                    ? "No cases match the current filters"
                    : isAdditionalStainsTab
                    ? "No additional stain cases pending cutting"
                    : "No cases pending cutting"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  {renderTableHeader()}
                  <Table.Tbody>
                    {activeCases.map((caseItem: any) =>
                      renderCaseRow(caseItem)
                    )}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>

          {/* Blocked Cases Table */}
          {blockedTotal > 0 && (
            <Paper p="md" withBorder>
              <Stack gap="md">
                <Group justify="space-between">
                  <Group gap="sm">
                    <IconAlertTriangle
                      size={20}
                      color="var(--mantine-color-red-6)"
                    />
                    <Text fw={500} size="lg" c="red">
                      Blocked Cases ({blockedTotal})
                    </Text>
                  </Group>
                  {totalBlockedPages > 1 && (
                    <Group gap="md">
                      <Text size="sm" c="dimmed">
                        Showing {(blockedPage - 1) * ITEMS_PER_PAGE + 1}-
                        {Math.min(blockedPage * ITEMS_PER_PAGE, blockedTotal)}{" "}
                        of {blockedTotal} cases
                      </Text>
                      <Pagination
                        total={totalBlockedPages}
                        value={blockedPage}
                        onChange={setBlockedPage}
                        size="sm"
                      />
                    </Group>
                  )}
                </Group>
                <Text size="sm" c="dimmed">
                  These cases have reported errors and are blocked from
                  proceeding until the issue is resolved.
                </Text>
                <Divider />
                <Table striped>
                  {renderTableHeader()}
                  <Table.Tbody>
                    {blockedCases.map((caseItem: any) =>
                      renderCaseRow(caseItem, true)
                    )}
                  </Table.Tbody>
                </Table>
              </Stack>
            </Paper>
          )}
        </Stack>

        {/* Filter Modal */}
        <FilterModal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          filters={filters}
          onApply={handleFiltersApply}
          allowedStatusNames={["Pending Cutting", "Cutting"]}
          showAdditionalStainFilter={!isAdditionalStainsTab}
        />
      </Box>
    </Container>
  );
}
